import { type ApiProblem, call } from '@rental/contracts/client'
import { Button, Field, Notice } from '@rental/ui'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { PageHeading, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { Frame } from './Frame'

/** First sign-in on a device: e-mail, password, and a TOTP code when the role asks for one. */
export function EnrollScreen({ reason }: { reason: 'new' | 'revoked' }) {
  const { messages: m } = useLocale()
  const { transition } = useAccess()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [totp, setTotp] = useState('')
  const [askTotp, setAskTotp] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const totpField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (askTotp) totpField.current?.focus()
  }, [askTotp])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/devices/enroll', {
        body: {
          email: email.trim(),
          password,
          deviceName: deviceName.trim(),
          ...(askTotp ? { totp: totp.trim() } : {}),
        },
      }),
    )
    setBusy(false)
    if (result.ok) {
      transition({ state: 'unlocked', session: result.data })
      if (result.data.pinMissing) void navigate({ to: '/pin' })
      return
    }
    if (result.problem.kind === 'api' && result.problem.code === 'totp_required') {
      setAskTotp(true)
    }
    if (result.problem.kind === 'api' && result.problem.code === 'totp_invalid') {
      // Typed again from the start, where the keyboard already is.
      setTotp('')
      totpField.current?.focus()
    }
    setProblem(result.problem)
  }

  return (
    <Frame>
      {/* The lock card's title and main button (UI-2g): every card before the PIN alike. */}
      <PageHeading className="text-xl">{m.enroll.title}</PageHeading>
      {reason === 'revoked' ? (
        <Notice tone="warning">
          <p>{m.enroll.revoked}</p>
        </Notice>
      ) : null}
      <p>{m.enroll.intro}</p>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={m.enroll.email} required>
          {(control) => (
            <StaffInput
              {...control}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label={m.enroll.password} required>
          {(control) => (
            <StaffInput
              {...control}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
        <Field label={m.enroll.deviceName} hint={m.enroll.deviceNameHint} required>
          {(control) => (
            <StaffInput
              {...control}
              maxLength={60}
              value={deviceName}
              onChange={(event) => setDeviceName(event.target.value)}
            />
          )}
        </Field>
        {askTotp ? (
          <Field label={m.enroll.totp} hint={m.enroll.totpHint} required>
            {(control) => (
              <StaffInput
                {...control}
                ref={totpField}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
        ) : null}
        <ProblemNotice problem={problem} />
        <Button type="submit" variant="staff" block busy={busy} className="shadow-action">
          {m.enroll.submit}
        </Button>
      </form>
    </Frame>
  )
}
